import type { CharacterInfo, RateLimitStatus } from '@gofranz/gpt-muse-common';
import { Box, Stack } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChatErrorBoundary } from '../components/Chat/ChatErrorBoundary';
import { ChatHeader } from '../components/Chat/ChatHeader';
import { ChatInterface } from '../components/Chat/ChatInterface';
import { ConversationModal } from '../components/Chat/ConversationModal';
import { useChatDatabase } from '../hooks/useChatDatabase';
import { useChatMessages } from '../hooks/useChatMessages';
import { useChatModels } from '../hooks/useChatModels';
import { useConversations } from '../hooks/useConversations';
import type { ChatMessage } from '../lib/chat-db';
import { chatService } from '../services/chatService';
import { useRustyState } from '../state';

export function ChatPage() {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitStatus | null>(null);
  const [isConversationModalOpen, setIsConversationModalOpen] = useState(false);
  const [selectedCharacter, setSelectedCharacter] = useState<string | null>(null);
  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [characters, setCharacters] = useState<CharacterInfo[]>([]);
  const [currentTokenUsage, setCurrentTokenUsage] = useState<{
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  } | null>(null);

  const isLoggedIn = useRustyState((state) => state.api?.auth?.getSession().isLoggedIn || false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Custom hooks for chat functionality
  const database = useChatDatabase();
  const conversations = useConversations();
  const messages = useChatMessages(conversations.currentConversationId);
  const models = useChatModels();

  useEffect(() => {
    fetchRateLimits();
    loadCharacterSettings();
    loadCharacters();
  }, []);

  // Get input placeholder based on selected character
  const getInputPlaceholder = useCallback(() => {
    if (selectedCharacter && selectedCharacter !== 'custom') {
      const character = characters.find((c) => c.id === selectedCharacter);
      if (character?.initial_message_suggestion) {
        return character.initial_message_suggestion;
      }
    }
    return 'Type your message here...';
  }, [selectedCharacter, characters]);

  // Load available characters
  const loadCharacters = useCallback(async () => {
    try {
      const api = useRustyState.getState().api;
      const charactersData = await api.listCharacters();
      setCharacters(charactersData);
    } catch (error) {
      console.error('Failed to load characters:', error);
    }
  }, []);

  // Load character settings from database
  const loadCharacterSettings = useCallback(async () => {
    try {
      const api = useRustyState.getState().api;
      if (api?.auth) {
        // For logged in users, we could load from server settings
        // For now, use IndexedDB for all users
      }

      // Load from IndexedDB
      const { chatDB } = await import('../lib/chat-db');
      await chatDB.init();
      const settings = await chatDB.getGlobalSettings();

      if (settings?.selectedCharacter) {
        setSelectedCharacter(settings.selectedCharacter);
      }
      if (settings?.customPrompt) {
        setCustomPrompt(settings.customPrompt);
      }
    } catch (error) {
      console.error('Failed to load character settings:', error);
    }
  }, []);

  // Save character settings to database
  const saveCharacterSettings = useCallback(
    async (character: string | null, prompt?: string) => {
      try {
        const { chatDB } = await import('../lib/chat-db');
        await chatDB.init();

        const currentSettings = await chatDB.getGlobalSettings();
        await chatDB.saveGlobalSettings({
          selectedModel: currentSettings?.selectedModel || models.selectedModel,
          selectedCharacter: character || undefined,
          customPrompt: prompt,
        });
      } catch (error) {
        console.error('Failed to save character settings:', error);
      }
    },
    [models.selectedModel]
  );

  // Fetch rate limits for unauthenticated users
  const fetchRateLimits = useCallback(async () => {
    if (!isLoggedIn) {
      try {
        const api = useRustyState.getState().api;
        const rateLimits = await api.getRateLimitStatus();
        setRateLimitInfo(rateLimits);
      } catch (error) {
        console.error('Failed to fetch rate limits:', error);
      }
    }
  }, [isLoggedIn]);

  // Handle chat memory limit changes
  const handleChatMemoryLimitChange = useCallback(async (limit: number) => {
    try {
      await chatService.saveChatMemoryLimit(limit);
    } catch (error) {
      console.error('Failed to save chat memory limit:', error);
    }
  }, []);

  // Handle model selection changes
  const handleModelChange = useCallback(
    async (modelId: string) => {
      await models.actions.select(modelId);

      // Update current conversation's model if there is one
      if (conversations.currentConversationId) {
        await conversations.actions.updateModel(conversations.currentConversationId, modelId);
      }
    },
    [models.actions, conversations.actions, conversations.currentConversationId]
  );

  // Handle character selection changes
  const handleCharacterChange = useCallback(
    async (characterId: string | null) => {
      setSelectedCharacter(characterId);

      // If custom character, keep existing custom prompt
      // If different character, clear custom prompt
      let prompt = '';
      if (characterId === 'custom') {
        prompt = customPrompt;
      } else {
        setCustomPrompt('');
      }

      // Save to global settings
      await saveCharacterSettings(characterId, prompt);

      // If we have a current conversation with no messages yet, update the conversation
      // so the character change is saved to the conversation before the first message
      const conversation = conversations.conversations.find(
        (conv) => conv.id === conversations.currentConversationId
      );
      if (conversation && conversation.messageCount === 0) {
        await conversations.actions.updateConversation(conversations.currentConversationId, {
          systemPromptType: characterId || undefined,
          systemPromptText: characterId === 'custom' ? prompt : undefined,
        });
      }

      // If this is a custom selection but we don't have a prompt,
      // the ChatHeader will handle opening the modal
    },
    [
      customPrompt,
      saveCharacterSettings,
      conversations.conversations,
      conversations.currentConversationId,
      conversations.actions,
    ]
  );

  // Process character query parameter
  useEffect(() => {
    const characterParam = searchParams.get('character');
    if (characterParam && characters.length > 0) {
      // Check if the character exists in the loaded characters
      const characterExists = characters.some((char) => char.id === characterParam);
      if (characterExists) {
        handleCharacterChange(characterParam);
        // Clear the query parameter to keep URL clean
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('character');
        setSearchParams(newSearchParams, { replace: true });
      }
    }
  }, [characters, searchParams, setSearchParams, handleCharacterChange]);

  // Load character from current conversation on initial load or when conversation changes
  useEffect(() => {
    if (conversations.currentConversationId && conversations.conversations.length > 0) {
      const currentConversation = conversations.conversations.find(
        (conv) => conv.id === conversations.currentConversationId
      );

      if (currentConversation) {
        // Load character from the current conversation
        const conversationCharacter = currentConversation.systemPromptType || null;
        const conversationCustomPrompt = currentConversation.systemPromptText || '';

        setSelectedCharacter(conversationCharacter);
        if (conversationCharacter === 'custom') {
          setCustomPrompt(conversationCustomPrompt);
        } else {
          setCustomPrompt('');
        }
      }
    }
  }, [conversations.currentConversationId, conversations.conversations]);

  // Create new conversation
  const handleNewConversation = useCallback(async () => {
    await conversations.actions.createNew(models.selectedModel, selectedCharacter, customPrompt);
  }, [conversations.actions, models.selectedModel, selectedCharacter, customPrompt]);

  // Handle new chat with custom model and character selection
  const handleNewChatConfirm = useCallback(
    async (modelId: string, characterId: string | null) => {
      // Temporarily update selections for this conversation
      if (modelId !== models.selectedModel) {
        await models.actions.select(modelId);
      }

      if (characterId !== selectedCharacter) {
        setSelectedCharacter(characterId);
        if (characterId === 'custom') {
          // Use existing custom prompt if any
        } else {
          setCustomPrompt('');
        }
      }

      // Create new conversation with the selected model and character
      await conversations.actions.createNew(
        modelId,
        characterId,
        characterId === 'custom' ? customPrompt : ''
      );
    },
    [models.actions, models.selectedModel, conversations.actions, selectedCharacter, customPrompt]
  );

  // Handle message deletion
  const handleDeleteMessage = useCallback(
    async (messageId: string) => {
      await messages.actions.delete(messageId);
      conversations.actions.updateMetadata(conversations.currentConversationId, -1);
    },
    [messages.actions, conversations.actions, conversations.currentConversationId]
  );

  // Handle conversation selection
  const handleSelectConversation = useCallback(
    async (conversationId: string) => {
      await conversations.actions.select(conversationId);

      // Find the conversation directly from the conversations array to avoid stale closure issues
      const conversation = conversations.conversations.find((conv) => conv.id === conversationId);

      // Update selected model if the conversation has a last selected model
      if (conversation?.lastSelectedModel) {
        await models.actions.select(conversation.lastSelectedModel);
      }

      // Load character from conversation or clear if none
      if (conversation) {
        // If conversation has no character, explicitly set to null (no character)
        const conversationCharacter = conversation.systemPromptType || null;
        const conversationCustomPrompt = conversation.systemPromptText || '';

        // Update character selection to match conversation (null if no character)
        setSelectedCharacter(conversationCharacter);

        // Only set custom prompt if the character is 'custom'
        if (conversationCharacter === 'custom') {
          setCustomPrompt(conversationCustomPrompt);
        } else {
          setCustomPrompt('');
        }

        // Save to global settings to maintain consistency
        await saveCharacterSettings(
          conversationCharacter,
          conversationCharacter === 'custom' ? conversationCustomPrompt : ''
        );
      }
    },
    [conversations.actions, conversations.conversations, models.actions, saveCharacterSettings]
  );

  // Handle conversation deletion
  const handleDeleteConversation = useCallback(
    async (conversationId: string) => {
      await conversations.actions.delete(conversationId);
    },
    [conversations.actions]
  );

  // Toggle conversation modal
  const handleToggleConversationModal = useCallback(() => {
    setIsConversationModalOpen((prev) => !prev);
  }, []);

  // Handle sending messages
  const handleSendMessage = useCallback(
    async (message: ChatMessage) => {
      if (conversations.currentConversationId) {
        const messageWithMetadata: ChatMessage = {
          ...message,
          conversationId: conversations.currentConversationId,
          model: models.selectedModel,
        };

        await messages.actions.send(messageWithMetadata);

        // Update conversation metadata and title if it's the first message
        conversations.actions.updateMetadata(conversations.currentConversationId, 1);

        // Update conversation title if it's the first user message
        const conversation = conversations.getCurrentConversation();
        if (conversation && conversation.messageCount === 0 && message.role === 'user') {
          await conversations.actions.updateTitle(
            conversations.currentConversationId,
            message.content
          );
        }
      }
    },
    [conversations, messages.actions, models.selectedModel]
  );

  // Handle receiving messages (streaming)
  const handleReceiveMessage = useCallback(
    (message: ChatMessage) => {
      if (conversations.currentConversationId) {
        const messageWithMetadata: ChatMessage = {
          ...message,
          conversationId: conversations.currentConversationId,
          model: models.selectedModel,
        };

        messages.actions.addStreaming(messageWithMetadata);
      }
    },
    [conversations.currentConversationId, messages.actions, models.selectedModel]
  );

  // Handle message completion (end of streaming)
  const handleMessageComplete = useCallback(
    async (message: ChatMessage) => {
      if (conversations.currentConversationId) {
        // The message is already in local state via handleReceiveMessage,
        // we just need to save it to database
        const messageWithMetadata = {
          ...message,
          conversationId: conversations.currentConversationId,
          model: models.selectedModel,
        };

        // Save to database without adding to local state again
        await messages.actions.saveToDatabase(messageWithMetadata);
        conversations.actions.updateMetadata(conversations.currentConversationId, 1);

        // If this is the first assistant message and we have a character selected,
        // save the character information to the conversation to lock it in
        const conversation = conversations.conversations.find(
          (conv) => conv.id === conversations.currentConversationId
        );
        if (
          conversation &&
          message.role === 'assistant' &&
          conversation.messageCount === 0 &&
          (selectedCharacter || customPrompt) &&
          !conversation.systemPromptType
        ) {
          await conversations.actions.updateConversation(conversations.currentConversationId, {
            systemPromptType: selectedCharacter || undefined,
            systemPromptText: selectedCharacter === 'custom' ? customPrompt : undefined,
          });
        }
      }
    },
    [conversations, messages.actions, models.selectedModel, selectedCharacter, customPrompt]
  );

  // Handle token usage updates from streaming
  const handleTokenUsage = useCallback(
    (usage: { prompt_tokens: number; completion_tokens: number; total_tokens: number }) => {
      setCurrentTokenUsage(usage);
    },
    []
  );

  // Get context window for the currently selected model
  const getCurrentModelContextWindow = useCallback((): number => {
    const selectedModelInfo = models.models.find((model) => model.id === models.selectedModel);
    return selectedModelInfo?.context_window || 0;
  }, [models.models, models.selectedModel]);

  // Auto-create first conversation if none exist and everything is loaded
  useEffect(() => {
    if (
      conversations.conversations.length === 0 &&
      !conversations.isLoading &&
      !models.isLoading &&
      database.isInitialized
    ) {
      handleNewConversation();
    }
  }, [
    conversations.conversations.length,
    conversations.isLoading,
    models.isLoading,
    database.isInitialized,
    handleNewConversation,
  ]);

  return (
    <ChatErrorBoundary>
      <Stack gap={0} h="100vh" style={{ overflow: 'hidden' }}>
        <ChatHeader
          onNewChat={handleNewConversation}
          onToggleSidebar={handleToggleConversationModal}
          rateLimitInfo={rateLimitInfo}
          isLoggedIn={isLoggedIn}
          isDesktop={Boolean(isDesktop)}
          models={models.models}
          selectedModel={models.selectedModel}
          selectedCharacter={selectedCharacter}
          characters={characters}
          currentTokenUsage={currentTokenUsage}
          contextWindow={getCurrentModelContextWindow()}
          onNewChatConfirm={handleNewChatConfirm}
        />

        <Box style={{ height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column' }}>
          {/* Conversation Modal */}
          <ConversationModal
            opened={isConversationModalOpen}
            onClose={handleToggleConversationModal}
            conversations={conversations.conversations}
            currentConversationId={conversations.currentConversationId}
            onSelectConversation={handleSelectConversation}
            onDeleteConversation={handleDeleteConversation}
          />

          {/* Chat Interface - Takes full space */}
          <ChatInterface
            messages={messages.messages}
            selectedModel={models.selectedModel}
            isAuthenticated={isLoggedIn}
            isLoading={messages.isLoading}
            onRateLimitUpdate={setRateLimitInfo}
            onTokenUsage={handleTokenUsage}
            onSendMessage={handleSendMessage}
            onReceiveMessage={handleReceiveMessage}
            onMessageComplete={handleMessageComplete}
            onDeleteMessage={handleDeleteMessage}
            selectedCharacter={selectedCharacter}
            customPrompt={customPrompt}
            rateLimitInfo={rateLimitInfo}
            onChatMemoryLimitChange={handleChatMemoryLimitChange}
            models={models.models}
            onModelSelect={handleModelChange}
            onCharacterSelect={handleCharacterChange}
            isLoadingModels={models.isLoading}
            inputPlaceholder={getInputPlaceholder()}
            characters={characters}
          />
        </Box>
      </Stack>
    </ChatErrorBoundary>
  );
}
